import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AuthModal } from '@/components/AuthModal';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/useAuthStore';
import { hasAuthToken } from '@/lib/authStorage';
import { onAuthSessionRequired, registerAuthModalOpener } from '@/lib/authEvents';
import { ensureFreshToken, forceLogout } from '@/lib/authSession';

interface AuthSessionProviderProps {
  children: React.ReactNode;
}

export function AuthSessionProvider({ children }: AuthSessionProviderProps) {
  const { isAuthenticated } = useAuthStore();
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => registerAuthModalOpener(() => setShowAuth(true)), []);

  useEffect(() => {
    return onAuthSessionRequired(() => {
      setShowAuth(true);
      toast.error('Session expirée. Reconnectez-vous pour continuer.');
    });
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !hasAuthToken()) return;

    let cancelled = false;

    const validateSession = async () => {
      const fresh = await ensureFreshToken();
      if (cancelled) return;

      if (!fresh) {
        await forceLogout();
        return;
      }

      try {
        await authApi.getProfile();
      } catch {
        if (!cancelled) await forceLogout();
      }
    };

    validateSession();

    const onFocus = () => {
      if (isAuthenticated && hasAuthToken()) {
        ensureFreshToken();
      }
    };

    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated]);

  return (
    <>
      {children}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={() => toast.success('Session rétablie.')}
      />
    </>
  );
}
